/**
 * filter data collection to get part of that
 * @date 2024/10/18
 */

import * as _ from 'lodash-es';

import { forceIntegerToDecimal as toDecimal } from '@/utils';

import { iconFinderXternal } from '../store';
import {
  GO,
  BPSymbol,
  SYMBOL_MODE,
  SymbolFindResult,
  LENGTH_TYPE,
  SPECIAL_JOB_ITEM as SPI,
  FILTERS,
  CATALOGUE_CATEGORY,
  CATALOGUE_TYPE,
  FIELD_W1 as W1,
  FIELD_H1 as H1,
  FIELD_D1 as D1,
  isRoundShape,
  isRectShape,
  isRoundRectShape,
  isRealDuct,
  isRealPipe,
} from '../types';

/**
 * Compose symbol detail with a single method
 * ! format manual added size value with decimal format
 * @date 2025/10/28
 *
 * @param detail symbol detail
 * @returns
 */
const detailToSize = (detail: GO) => {
  const w = toDecimal(detail[W1]);
  const h = toDecimal(detail[H1]);
  const d = toDecimal(detail[D1]);
  // 1st level
  // const itemType = detail.itemType as string;
  // 2nd level
  const category: string | undefined = detail.category;
  // ! categrory could be `undefined` so treat it as empty
  // @2025/09/16
  if (!category) return FILTERS.EMPTY;

  const initial = category.substring(0, 1);
  const shape = detail.shape as string;
  // ! back-compatible shape check!
  // @2025/09/09
  const isDuct = isRealDuct(category);
  const isPipe = isRealPipe(category);
  const isRound = isRoundShape(shape) || isPipe;
  const isRect = isRectShape(shape);
  const isRR = isRoundRectShape(shape);
  if (w && h && d && isRR) {
    return `${w}*${h}*${d}`;
  }
  if (w && h && isRect) {
    return `${w}*${h}`;
  }
  // ! allow PIPE fitting display with diffrent format
  // @2025/09/09
  const prefix = isDuct ? 'D' : isPipe ? 'P' : initial;
  if (d && isRound) {
    return `${prefix}-${d}`;
  }
  return FILTERS.EMPTY;
};

/**
 * Get size groups from fitting symbols
 *
 * @param symbols symbols in current drawing
 * @returns size list
 */
export const composeSizesFrom = (symbols: BPSymbol[]) => {
  const sizes = new Set<string>();
  symbols.forEach(({ detail }) => {
    const isFitting = detail.itemType == CATALOGUE_TYPE.F;
    if (isFitting) {
      sizes.add(detailToSize(detail));
    }
    // ! consider `DUCT_LENGTH` - @2025/08/27
    const isLengh = detail.itemType == CATALOGUE_TYPE.L;
    if (isLengh) {
      sizes.add(detailToSize(detail));
    }
  });
  const sizeList = Array.from(sizes);
  const finalList = sizeList.sort();
  // remove last `empty`
  finalList.pop();

  const sizeGroups = {
    fixed: [FILTERS.ALL, FILTERS.EMPTY],
    rectangular: finalList.filter((s) => s.includes('*')),
    round: finalList.filter((s) => s.startsWith('D')),
    pipe: finalList.filter((s) => s.startsWith('P')),
  };

  return sizeGroups;
};

/**
 * check size match or not
 * @param detail symbol detail
 * @param sizeFilter size
 * @returns if match
 */
export const isSizeMatch = (detail: GO, sizeFilter: string) => {
  return detailToSize(detail) == sizeFilter;
};

/**
 * default option from system filter at top section of drawing
 */
export const ALL_SYSTEMS = {
  label: 'All Systems',
  value: '*',
};

/**
 * Filter out symbol by `system` property
 * @param smb symbol instance
 * @param system symbol type selected
 * @returns
 */
export const systemTypeFilter = (smb: BPSymbol, system: string) => {
  if (system == ALL_SYSTEMS.value) return true;
  if (smb.detail['system'] == system) return true;
  return false;
};

/**
 * DUCT|FITTING filter
 * @param smb symbol
 * @returns
 */
export const ductFittingFilter = (smb: BPSymbol) =>
  smb.category === 'DUCT' && smb.detail.itemType == 'FITTING';

/**
 * PIPE|FITTING filter
 * @param smb symbol
 * @returns
 */
export const pipeFittingFilter = (smb: BPSymbol) =>
  smb.category === 'PIPE' && smb.detail.itemType == 'FITTING';

/**
 * DUCT|LENGTH filter
 * FIXME: update `category` property for duct_length
 * @param smb symbol
 * @returns
 */
export const ductLengthFilter = (smb: BPSymbol) => {
  if (smb.class === SPI.RECT_DUCT) return true;
  if (smb.category === LENGTH_TYPE.D) return true;
  const isDuct = smb.category === CATALOGUE_CATEGORY.D;
  const isLength = smb.detail['itemType'] === CATALOGUE_TYPE.L;
  return isDuct && isLength;
};

/**
 * PIPE|LENGTH filter
 * FIXME: update `category` property for pipe_length
 * @param smb symbol
 * @returns
 */
export const pipeLengthFilter = (smb: BPSymbol) => {
  if (smb.category === LENGTH_TYPE.P) return true;
  const isPipe = smb.category === CATALOGUE_CATEGORY.P;
  const isLength = smb.detail['itemType'] === CATALOGUE_TYPE.L;
  return isPipe && isLength;
};

/**
 * Pipe Lengths and Elbows (Pipe Length + Pipe Elbows)
 * @deprecated on 2025/02/19 in favor of `pipeLengthElbowAndTjunctionFilter`
 * @param smb symbol in drawing
 * @returns true|false
 * @date 2024/12/19
 */
export const pipeLengthAndElbowFilter = (smb: BPSymbol) => {
  if (smb.category === LENGTH_TYPE.P) return true;
  const isPipe = smb.category === CATALOGUE_CATEGORY.P;
  const isLength = smb.detail['itemType'] === CATALOGUE_TYPE.L;
  if (isPipe && isLength) return true;
  if (smb.class === SPI.EBPI) return true;
  return false;
};

/**
 * filtering 3 type of symbols:
 * - Pipe Length
 * - Pipe Elbow
 * - T junction
 * @param smb
 * @returns
 */
export const pipeLengthElbowAndTjunctionFilter = (smb: BPSymbol) => {
  if (smb.category === LENGTH_TYPE.P) return true;
  const isPipe = smb.category === CATALOGUE_CATEGORY.P;
  const isLength = smb.detail['itemType'] === CATALOGUE_TYPE.L;
  if (isPipe && isLength) return true;
  if (smb.class === SPI.EBPI) return true;
  if (smb.class === SPI.TJCT) return true;
  return false;
};

/**
 * EQUIPMENT filter
 * @param smb symbol
 * @returns
 */
export const equipmentsFilter = (smb: BPSymbol) =>
  smb.category === CATALOGUE_CATEGORY.E;
/**
 * PLUMBING|FITTING filter
 * @param symbol symbol
 * @returns
 */
export const plumbingFilter = (symbol: BPSymbol) => {
  const isPlumbing = symbol.category === CATALOGUE_CATEGORY.PLM;
  const isFitting = symbol.detail['itemType'] === CATALOGUE_TYPE.F;
  return isPlumbing && isFitting;
};

export const symbolToFR = (smbl: BPSymbol): SymbolFindResult => {
  const icon = iconFinderXternal(_.snakeCase(smbl.class));
  const hashStr = smbl.index.toString();
  const system = smbl.detail['system'] || '';
  return { ...smbl, icon, hash: hashStr, system };
};

const symbolToListItems = (results: BPSymbol[]): SymbolFindResult[] => {
  return results.map(symbolToFR);
};

/**
 * filter all symbols with specific type
 * FIXME: but for equipment it represent equipment `name` - 2023/05/26
 *
 * @param type_or_name item type or equipment name
 * @returns
 */
const filteringSymbolsBy = (
  type_or_name: string,
  symbols: BPSymbol[],
): SymbolFindResult[] => {
  const results = symbols.filter((symbol) => {
    const isTypeMatch = symbol.class === type_or_name;
    const isNameMatch = symbol.detail?.['name'] === type_or_name;
    return isTypeMatch || isNameMatch;
  });
  if (type_or_name === 'ALL') return symbolToListItems(symbols);
  return symbolToListItems(results);
};

/**
 * filter symbols just by item type in review table
 *
 * @param type item type
 * @param symbols
 * @returns
 */
export const pickingSymbolJustBy = (type: string, symbols: BPSymbol[]) => {
  const visibleFilter = (smb: BPSymbol) => smb.mode !== SYMBOL_MODE.DEL;
  const visibleSymbols = symbols.filter(visibleFilter);
  return filteringSymbolsBy(type, visibleSymbols);
};

/**
 * Filter out symbol rows with first level filter in total review panel
 * @param category filter type of first dropdown select such as: DUCT|FITTING
 * @param symbols source symbols
 * @returns filtered result
 */
export const pickupSymbolsByFilter = (
  category: string,
  symbols: BPSymbol[],
): SymbolFindResult[] => {
  const visibleFilter = (smb: BPSymbol) => smb.mode !== SYMBOL_MODE.DEL;
  const visibleSymbols = symbols.filter(visibleFilter);

  // setup dynamic filter by group
  const groupFilter: { [key: string]: (smb: BPSymbol) => boolean } = {
    [FILTERS.DUCTS]: ductFittingFilter,
    [FILTERS.PIPES]: pipeFittingFilter,
    [FILTERS.DLENT]: ductLengthFilter,
    [FILTERS.PLENT]: pipeLengthFilter,
    [FILTERS.EQMT]: equipmentsFilter,
    [FILTERS.PLMBS]: plumbingFilter,
  };
  const nothing = () => false;
  const safeFilter = groupFilter[category] || nothing;
  const allSymbolsInGroup = visibleSymbols.filter(safeFilter);
  return symbolToListItems(allSymbolsInGroup);
};
